"use client";

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  useSession,
  signOut as authSignOut,
  Session,
  User,
  betterAuthClient,
} from "@/lib/auth-client";
import { _updateCachedToken, clearAuthCookies } from "@/lib/auth";
import { createHiveClient } from "@/lib/supabase/client";

interface Organization {
  id: string;
  name: string;
  slug?: string;
  logo?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  organization: Organization | null;
  isLoading: boolean;
  error: Error | null;
  isAdmin: boolean;
  isOrgUser: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  hasPermission: (permission: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userMetadata, setUserMetadata] = useState<any>(null);
  const hasAttemptedSessionCleanup = useRef(false);

  // Use Better Auth's reactive useSession hook
  const {
    data: sessionData,
    isPending,
    error: sessionError,
    refetch,
  } = useSession();

  // Handle stale session cookies - if session returns error or null after loading,
  // and we have cookies but no valid session, clear everything and redirect to login
  useEffect(() => {
    // Only run once after initial load completes
    if (isPending || hasAttemptedSessionCleanup.current) return;

    // Check if we have a session cookie but no valid session
    const hasCookie = document.cookie.includes("better-auth.session_token");
    const hasSession = !!sessionData?.user;
    const hasError = !!sessionError;

    if (hasCookie && (!hasSession || hasError)) {
      hasAttemptedSessionCleanup.current = true;
      console.log(
        "[AuthProvider] Stale session detected - clearing auth state"
      );

      // Clear all auth state
      _updateCachedToken("");
      clearAuthCookies();

      // Also call server action to clear HttpOnly cookies
      import("@/app/(auth)/actions").then(({ clearAuthSession }) => {
        clearAuthSession().then(() => {
          // Redirect to login if not already there
          if (
            typeof window !== "undefined" &&
            !window.location.pathname.startsWith("/login")
          ) {
            window.location.href = "/login";
          }
        });
      });
    }
  }, [isPending, sessionData?.user, sessionError]);

  // Debug session state (only log significant changes in development)
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && sessionData?.user?.id) {
      console.log("[AuthProvider] User authenticated:", sessionData.user.id);
    }
  }, [sessionData?.user?.id]);

  // Map Better Auth session to our User type with enhanced metadata
  const user: User | null = useMemo(() => {
    if (!sessionData?.user) return null;

    const betterAuthUser = sessionData.user as any;

    const mappedUser = {
      id: betterAuthUser.id,
      email: betterAuthUser.email,
      name: betterAuthUser.name ?? userMetadata?.display_name ?? undefined,
      avatar_url: betterAuthUser.image ?? userMetadata?.avatar_url ?? undefined,
      role: betterAuthUser.role ?? userMetadata?.role ?? "user",
      organizationId: userMetadata?.organization_id,
      user_metadata: userMetadata,
    };

    return mappedUser;
  }, [sessionData?.user, userMetadata]);

  // Map to our Session type with organization context
  const session: Session | null = useMemo(() => {
    if (!user) {
      return null;
    }
    return {
      user,
      token: sessionData?.session?.token,
      activeOrganizationId: organization?.id,
    };
  }, [user, sessionData?.session?.token, organization]);

  // Fetch user metadata from Supabase when session changes
  useEffect(() => {
    async function fetchUserData() {
      if (!sessionData?.user?.id) {
        setUserMetadata(null);
        setOrganization(null);
        return;
      }

      try {
        const supabase = createHiveClient();

        // Fetch user metadata from ng_users table
        const { data: userData, error: userError } = await supabase
          .from("ng_users")
          .select("*")
          .eq("id", sessionData.user.id)
          .single();

        if (userError) {
          // Only log if not a common expected error (user not in ng_users or Supabase unavailable)
          const isExpectedError =
            userError.code === "PGRST116" || // Row not found
            userError.message?.includes("Invalid authentication") ||
            userError.message?.includes("fetch failed") ||
            userError.message?.includes("Failed to fetch");

          if (!isExpectedError && process.env.NODE_ENV === "development") {
            console.warn(
              "[AuthProvider] Could not fetch user metadata:",
              userError.message
            );
          }
          // User might not exist in ng_users yet or Supabase unavailable - this is okay
          // Better Auth user exists, but extended metadata doesn't
          setUserMetadata(null);
          setOrganization(null);
        } else if (userData) {
          setUserMetadata(userData);

          // Set organization from user metadata if available
          if (userData.organization_id) {
            setOrganization({
              id: userData.organization_id,
              name: userData.organization_id, // Using ID as name since no org table exists
            });
          } else {
            setOrganization(null);
          }
        }
      } catch (error) {
        console.error(
          "[AuthProvider] Error fetching user data from Supabase:",
          error
        );
        setUserMetadata(null);
        setOrganization(null);
      }
    }

    fetchUserData();
  }, [sessionData?.user?.id]);

  // Sync session token with cached token for API calls
  useEffect(() => {
    const token = sessionData?.session?.token;
    if (token) {
      _updateCachedToken(token);
    } else {
      _updateCachedToken("");
    }
  }, [sessionData?.session?.token]);

  const handleSignOut = useCallback(async () => {
    setUserMetadata(null);
    setOrganization(null);
    _updateCachedToken("");
    await authSignOut();
  }, []);

  const refreshSession = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Compute admin and org user status
  // Admin = local role "admin" OR hub_role "admin" (set from mstroHub sync for org owners)
  const isAdmin = useMemo(() => {
    return user?.role === "admin" || userMetadata?.hub_role === "admin";
  }, [user?.role, userMetadata?.hub_role]);

  const isOrgUser = useMemo(() => {
    return !!organization?.id || !!userMetadata?.organization_id;
  }, [organization?.id, userMetadata?.organization_id]);

  // Check permissions from user metadata or role
  const checkPermission = useCallback(
    async (permission: string) => {
      if (!user) return false;

      // Admin role has all permissions
      if (user.role === "admin") return true;

      // Organization users have elevated permissions
      if (isOrgUser && permission !== "super_admin") return true;

      // Check user metadata for specific permissions
      if (userMetadata?.permissions?.includes(permission)) return true;

      // Default: authenticated users have basic access
      return !!user;
    },
    [user, userMetadata, isOrgUser]
  );

  const value: AuthContextType = useMemo(
    () => ({
      user,
      session,
      organization,
      isLoading: isPending,
      error: sessionError ? new Error(sessionError.message) : null,
      isAdmin,
      isOrgUser,
      signOut: handleSignOut,
      refreshSession,
      hasPermission: checkPermission,
    }),
    [
      user,
      session,
      organization,
      isPending,
      sessionError,
      isAdmin,
      isOrgUser,
      handleSignOut,
      refreshSession,
      checkPermission,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Re-export the Better Auth client for direct access if needed
export { betterAuthClient };
